import { useEffect, useEffectEvent, useRef, useState } from "react";
import { Loader2, Plus, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useApiSWR } from "@/hooks/useApiSWR";
import {
	type TaskAttachment,
	listTaskAttachments,
	uploadTaskAttachment,
	deleteTaskAttachment,
} from "@/services/task-attachment.service";
import { Skeleton } from "../ui/skeleton";
import { AttachmentPreviewModal } from "./AttachmentPreviewModal";

const MAX_BYTES = 10 * 1024 * 1024;

const ALLOWED_MIME = new Set([
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/msword",
	"application/vnd.ms-excel",
	"text/plain",
	"application/zip",
	"application/x-zip-compressed",
]);

const ACCEPT_ATTR =
	".jpg,.jpeg,.png,.webp,.gif,.pdf,.docx,.xlsx,.doc,.xls,.txt,.zip";

function isImage(mime: string): boolean {
	return mime.startsWith("image/");
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
	projectId: string;
	taskId: string;
	canEdit: boolean;
	/** Upload files pasted from the clipboard while mounted. */
	pasteEnabled?: boolean;
}

// Paste targets that keep native paste: text fields, rich editors, nested dialogs.
const PASTE_IGNORE_SELECTOR =
	'input, textarea, [contenteditable="true"], [role="dialog"]';

export function TaskAttachments({
	projectId,
	taskId,
	canEdit,
	pasteEnabled = false,
}: Props) {
	const {
		data,
		error: loadError,
		isLoading: loading,
		mutate,
	} = useApiSWR<TaskAttachment[]>(["tasks", taskId, "attachments"], () =>
		listTaskAttachments(projectId, taskId),
	);
	const items = data ?? [];
	const [uploading, setUploading] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	function openPicker() {
		inputRef.current?.click();
	}

	async function handleFiles(files: FileList | File[] | null) {
		if (!files || files.length === 0) return;

		const valid: File[] = [];
		for (const f of Array.from(files)) {
			if (!ALLOWED_MIME.has(f.type)) {
				toast.error("Unsupported file type", { description: f.name });
				continue;
			}
			if (f.size > MAX_BYTES) {
				toast.error("File too large", {
					description: `${f.name} exceeds 10 MB`,
				});
				continue;
			}
			valid.push(f);
		}

		if (valid.length === 0) return;

		setUploading(true);
		try {
			for (const file of valid) {
				try {
					const created = await uploadTaskAttachment(
						projectId,
						taskId,
						file,
					);
					await mutate((curr) => [created, ...(curr ?? [])], {
						revalidate: false,
					});
					toast.success("Attachment uploaded", {
						description: file.name,
					});
				} catch (e) {
					toast.error("Upload failed", {
						description: (e as Error).message || file.name,
					});
				}
			}
		} finally {
			setUploading(false);
			if (inputRef.current) inputRef.current.value = "";
			await mutate();
		}
	}

	const onPaste = useEffectEvent((e: ClipboardEvent) => {
		if (e.defaultPrevented || uploading) return;
		const target = e.target as HTMLElement | null;
		if (target?.closest?.(PASTE_IGNORE_SELECTOR)) return;
		const files = e.clipboardData?.files;
		if (!files?.length) return;
		e.preventDefault();
		handleFiles(Array.from(files));
	});

	useEffect(() => {
		if (!pasteEnabled || !canEdit) return;
		window.addEventListener("paste", onPaste);
		return () => window.removeEventListener("paste", onPaste);
	}, [pasteEnabled, canEdit]);

	async function handleDelete(att: TaskAttachment) {
		if (!window.confirm(`Delete "${att.file_name}"?`)) return;
		setDeletingId(att.id);
		if (previewIndex !== null && items[previewIndex]?.id === att.id) {
			setPreviewIndex(null);
		}
		await mutate((curr) => (curr ?? []).filter((a) => a.id !== att.id), {
			revalidate: false,
		});
		try {
			await deleteTaskAttachment(projectId, taskId, att.id);
			toast.success("Attachment deleted");
		} catch (e) {
			toast.error("Delete failed", {
				description: (e as Error).message,
			});
		} finally {
			setDeletingId(null);
			await mutate();
		}
	}

	if (loading) {
		return (
			<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
				<Skeleton className="aspect-4/3" />
				<Skeleton className="aspect-4/3" />
				<Skeleton className="aspect-4/3" />
			</div>
		);
	}

	return (
		<>
			{loadError && (
				<p className="text-xs text-danger mb-2">
					Failed to load attachments.{" "}
					{loadError.message}
				</p>
			)}
			<input
				ref={inputRef}
				type="file"
				multiple
				accept={ACCEPT_ATTR}
				className="hidden"
				onChange={(e) => handleFiles(e.target.files)}
			/>
			<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
				{items.map((att, idx) => (
					<AttachmentTile
						key={att.id}
						attachment={att}
						canDelete={canEdit}
						deleting={deletingId === att.id}
						onOpen={() => setPreviewIndex(idx)}
						onDelete={() => handleDelete(att)}
					/>
				))}
				{canEdit && (
					<button
						type="button"
						onClick={openPicker}
						disabled={uploading}
						className={cn(
							"aspect-[4/3] rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted hover:border-primary hover:text-primary transition-colors",
							uploading && "opacity-60 cursor-not-allowed",
						)}
					>
						{uploading ? (
							<>
								<Loader2 className="h-5 w-5 mb-1 animate-spin" />
								<span className="text-xs font-medium">
									Uploading...
								</span>
							</>
						) : (
							<>
								<Plus className="h-5 w-5 mb-1" />
								<span className="text-xs font-medium">
									Add New
								</span>
								{pasteEnabled && (
									<span className="text-[10px] mt-0.5">
										or paste
									</span>
								)}
							</>
						)}
					</button>
				)}
			</div>
			{items.length === 0 && !canEdit && (
				<p className="text-[11px] text-muted mt-2 italic">
					No attachments.
				</p>
			)}
			<AttachmentPreviewModal
				items={items}
				index={previewIndex}
				onIndexChange={setPreviewIndex}
				onClose={() => setPreviewIndex(null)}
			/>
		</>
	);
}

function AttachmentTile({
	attachment,
	canDelete,
	deleting,
	onOpen,
	onDelete,
}: {
	attachment: TaskAttachment;
	canDelete: boolean;
	deleting: boolean;
	onOpen: () => void;
	onDelete: () => void;
}) {
	const image = isImage(attachment.mime_type);

	return (
		<div className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted-subtle/40">
			<button
				type="button"
				onClick={onOpen}
				className="block h-full w-full cursor-pointer"
				title={attachment.file_name}
			>
				{image ? (
					<img
						src={attachment.file_url}
						alt={attachment.file_name}
						className="h-full w-full object-cover"
					/>
				) : (
					<div className="h-full w-full flex flex-col items-center justify-center p-3 text-center">
						<FileText className="h-7 w-7 text-muted mb-1.5" />
						<span className="text-[11px] font-medium text-foreground truncate w-full">
							{attachment.file_name}
						</span>
						<span className="text-[10px] text-muted mt-0.5">
							{formatSize(attachment.file_size)}
						</span>
					</div>
				)}
			</button>
			{canDelete && (
				<button
					type="button"
					onClick={onDelete}
					disabled={deleting}
					aria-label={`Delete ${attachment.file_name}`}
					className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-danger transition-opacity flex items-center justify-center disabled:opacity-60"
				>
					{deleting ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<X className="h-3 w-3" />
					)}
				</button>
			)}
		</div>
	);
}
